import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  TransactionFilters,
  DEFAULT_FILTERS,
  CATEGORY_OPTIONS,
  STATUS_OPTIONS,
  PERIOD_OPTIONS,
} from "../../filter-page/history.filters";
import styles from "../../history.module.scss";

interface Chip {
  key: string;
  label: string;
  clear: Partial<TransactionFilters>;
}

const fmt = (d: Date | null) =>
  d ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "…";

type FilterOption = { label: string; value: number | null };

function buildChips(filters: TransactionFilters, categoryOptions: ReadonlyArray<FilterOption>): Chip[] {
  const chips: Chip[] = [];

  const category = categoryOptions.find((o) => o.value === filters.category);
  if (category?.value != null) {
    chips.push({ key: "category", label: category.label, clear: { category: null } });
  }

  const status = STATUS_OPTIONS.find((o) => o.value === filters.status);
  if (status?.value != null) {
    chips.push({ key: "status", label: status.label, clear: { status: null } });
  }

  const period = PERIOD_OPTIONS.find((o) => o.value === filters.period);
  if (period?.value != null) {
    chips.push({ key: "period", label: period.label, clear: { period: null, startDate: null, endDate: null } });
  } else if (filters.startDate || filters.endDate) {
    chips.push({
      key: "dateRange",
      label: `${fmt(filters.startDate)} – ${fmt(filters.endDate)}`,
      clear: { startDate: null, endDate: null },
    });
  }

  return chips;
}

interface ActiveFilterChipsProps {
  filters: TransactionFilters;
  onChange: (filters: TransactionFilters) => void;
  // Override the category options used to resolve the category chip label.
  categoryOptions?: ReadonlyArray<FilterOption>;
}

export const ActiveFilterChips = ({ filters, onChange, categoryOptions = CATEGORY_OPTIONS }: ActiveFilterChipsProps) => {
  const { t } = useTranslation();
  const chips = useMemo(() => buildChips(filters, categoryOptions), [filters, categoryOptions]);

  if (chips.length === 0) return null;

  return (
    <div className={styles.activeFilters}>
      {chips.map((chip) => (
        <span key={chip.key} className={styles.filterChip}>
          {chip.label}
          <button
            className={styles.filterChipRemove}
            onClick={() => onChange({ ...filters, ...chip.clear })}
          >
            ✕
          </button>
        </span>
      ))}
      <button className={styles.clearAllBtn} onClick={() => onChange(DEFAULT_FILTERS)}>
        {t("history.clearAll")}
      </button>
    </div>
  );
};
